import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { StatsWeekTraining } from '../../../../../models/stats.models';
import {
  formatDurationHHmm,
  getElevationMeasurementUnit,
  getMeasurementUnit,
  getSpeedMeasurementUnit,
} from '../../../../../helpers/general.helpers';
import { formatZoneDurationCell } from '../../../../../components/period-summary/period-summary.helpers';
import { ZONE_CONFIGS } from '../../../../../helpers/general.helpers';
import { useTrainingLogContext } from '../../../training-log.context';
import LogFocusBadge from './log-focus-badge.component';
import { formatTrainingTime, getRpeDotColor, getTrainingSchedulePath } from '../log.helpers';

interface LogSessionDetailProps {
  training: StatsWeekTraining;
}

const LogSessionDetail: FC<LogSessionDetailProps> = ({ training }) => {
  const distanceUnit = getMeasurementUnit();
  const elevationUnit = getElevationMeasurementUnit();
  const speedUnit = getSpeedMeasurementUnit();
  const { hideAnnualStartDate, onOpenAthleteTraining } = useTrainingLogContext();
  const isLinkActive = !hideAnnualStartDate;
  const isCoachTrainingClickable = hideAnnualStartDate && !!onOpenAthleteTraining;
  const { workoutData } = training;
  const teamLabel = training.team?.name?.trim() || 'Individual Workout';
  const teamClass = training.team?.name ? 'team' : 'solo';

  return (
    <div className="log-sess-row">
      <div className="log-sess-top">
        <div className="log-sess-top-left">
          {isLinkActive ? (
            <Link to={getTrainingSchedulePath(training.id)} className="log-sess-name log-training-title-link">
              {training.title}
            </Link>
          ) : isCoachTrainingClickable ? (
            <button
              type="button"
              className="log-sess-name log-training-title-link log-training-title-btn"
              onClick={() => onOpenAthleteTraining?.(training.id)}
            >
              {training.title}
            </button>
          ) : (
            <span className="log-sess-name">{training.title}</span>
          )}
          <div className="log-sess-mode">
            <span>
              {training.sport} · {training.sportType}
            </span>
            <LogFocusBadge focus={training.focus} />
            <span className={`log-sess-team-tag ${teamClass}`}>· {teamLabel}</span>
          </div>
          <div className="log-sess-meta">
            {formatTrainingTime(training.time)}
            {training.location ? ` · ${training.location}` : ''}
          </div>
        </div>
      </div>
      <div className="log-sess-chips">
        <div className="log-sess-chip">
          <span className="log-sc-icon">⏱</span>
          <span className="log-sc-val">{formatDurationHHmm(workoutData.totalDurationComplete)}</span>
          <span className="log-sc-unit"> h</span>
        </div>
        {(workoutData.totalDistanceComplete ?? 0) > 0 && (
          <div className="log-sess-chip">
            <span className="log-sc-icon">📏</span>
            <span className="log-sc-val">{Math.round(workoutData.totalDistanceComplete)}</span>
            <span className="log-sc-unit"> {distanceUnit}</span>
          </div>
        )}
        {workoutData.totalElevationGain !== null && workoutData.totalElevationGain > 0 && (
          <div className="log-sess-chip">
            <span className="log-sc-icon">⛰</span>
            <span className="log-sc-val">{Math.round(workoutData.totalElevationGain)}</span>
            <span className="log-sc-unit"> {elevationUnit}</span>
          </div>
        )}
        {workoutData.averageHeartRate > 0 && (
          <div className="log-sess-chip">
            <span className="log-sc-icon">♥</span>
            <span className="log-sc-lbl">Avg</span>
            <span className="log-sc-val">{workoutData.averageHeartRate}</span>
            <span className="log-sc-unit"> bpm</span>
          </div>
        )}
        {workoutData.maxHeartRate > 0 && (
          <div className="log-sess-chip">
            <span className="log-sc-icon">♥</span>
            <span className="log-sc-lbl">Max</span>
            <span className="log-sc-val">{workoutData.maxHeartRate}</span>
            <span className="log-sc-unit"> bpm</span>
          </div>
        )}
        {workoutData.averageSpeed !== null && workoutData.averageSpeed > 0 && (
          <div className="log-sess-chip">
            <span className="log-sc-icon">⚡</span>
            <span className="log-sc-val">{workoutData.averageSpeed.toFixed(1)}</span>
            <span className="log-sc-unit"> {speedUnit}</span>
          </div>
        )}
        {workoutData.effortRating !== null && workoutData.effortRating > 0 && (
          <div className="log-sess-chip">
            <span className="log-sess-rpe-dot" style={{ background: getRpeDotColor(workoutData.effortRating) }} />
            <span className="log-sc-lbl">Effort</span>
            <span className="log-sc-val">{workoutData.effortRating}</span>
            <span className="log-sc-unit"> /10</span>
          </div>
        )}
      </div>
      <div className="log-day-zone-strip">
        {ZONE_CONFIGS.map((zone, index) => {
          const duration = workoutData.durationComplete?.[index] ?? 0;
          const distance = workoutData.distanceComplete?.[index] ?? 0;
          const isEmpty = duration <= 0 && distance <= 0;

          return (
            <div key={zone.label} className={`log-dz-item ${isEmpty ? 'log-dz-empty' : ''}`}>
              <div className="log-dz-dot" style={{ background: zone.barColor }} />
              <span className="log-dz-val">{`${zone.label} ${formatZoneDurationCell(duration)}`}</span>
              <span className="log-dz-km">
                {distance > 0
                  ? `${distance % 1 === 0 ? distance : distance.toFixed(1)}${distanceUnit}`
                  : `0${distanceUnit}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LogSessionDetail;
